import type { OrganizationProfile } from '@pulsify/shared'

/**
 * What the dashboard works on: the whole organization while profiles are off, else exactly one
 * place or one organizer profile.
 */
export type ProfileScope =
  | { kind: 'organization' }
  | { kind: 'place'; profileId: string; listingId: string }
  | { kind: 'organizer'; profileId: string }

export function profileScopeOf(
  enabled: boolean,
  active: Pick<OrganizationProfile, 'id' | 'kind' | 'listingId'> | null,
): ProfileScope {
  if (!enabled || !active) return { kind: 'organization' }
  if (active.kind === 'place' && active.listingId) {
    return { kind: 'place', profileId: active.id, listingId: active.listingId }
  }
  return { kind: 'organizer', profileId: active.id }
}

/** The places a page may offer: all of them for the organization, only its own for a profile. */
export function listingsInScope<T extends { id: string }>(
  listings: readonly T[],
  scope: ProfileScope,
): T[] {
  if (scope.kind === 'organization') return [...listings]
  if (scope.kind === 'place') return listings.filter((listing) => listing.id === scope.listingId)
  return []
}

/** Whether a place id handed in from elsewhere (a link, a copied event) belongs to the scope. */
export function listingInScope(listingId: string | null | undefined, scope: ProfileScope): boolean {
  if (!listingId) return false
  if (scope.kind === 'organization') return true
  return scope.kind === 'place' && scope.listingId === listingId
}

const PLACE_PAGE = /^\/dashboard\/listings\/([^/]+)(\/.*)?$/
const CROSSPOSTING_PAGE = /^\/dashboard\/crossposting\/([^/]+)(\/.*)?$/

function normalize(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
}

function setupHref(kind: 'place' | 'organizer', manual: boolean): string {
  return `/setup/profile?kind=${kind}${manual ? '&manual=true' : ''}`
}

const isTrue = (value: unknown) => value === true || value === 'true'

/**
 * Where a dashboard URL has to go so it only ever shows the active profile: creating something
 * new leads to the full-screen setup, another place's page to the active place's page, a place
 * page under an organizer profile to the overview. Null when the URL already fits.
 */
export function profileRedirect(
  rawPathname: string,
  search: Record<string, unknown>,
  scope: ProfileScope,
): string | null {
  if (scope.kind === 'organization') return null
  const pathname = normalize(rawPathname)

  if (pathname === '/dashboard/listings/create') return setupHref('place', isTrue(search.manual))
  if (pathname === '/dashboard/organizer' && isTrue(search.new)) {
    return setupHref('organizer', false)
  }

  if (scope.kind === 'organizer') {
    if (
      pathname === '/dashboard/listings' ||
      PLACE_PAGE.test(pathname) ||
      pathname === '/dashboard/crossposting' ||
      CROSSPOSTING_PAGE.test(pathname) ||
      pathname === '/dashboard/menu' ||
      pathname === '/dashboard/actions'
    ) {
      return '/dashboard'
    }
    if (pathname === '/dashboard/business/edit') return '/dashboard/organizer'
    return null
  }

  const own = scope.listingId
  if (pathname === '/dashboard/listings' || pathname === '/dashboard/organizer') {
    return `/dashboard/listings/${own}`
  }
  if (pathname === '/dashboard/business/edit') return `/dashboard/listings/${own}/edit`
  if (pathname === '/dashboard/crossposting') return `/dashboard/crossposting/${own}`

  for (const [pattern, base] of [
    [PLACE_PAGE, '/dashboard/listings'],
    [CROSSPOSTING_PAGE, '/dashboard/crossposting'],
  ] as const) {
    const match = pattern.exec(pathname)
    if (match && match[1] !== own) return `${base}/${own}${match[2] ?? ''}`
  }

  if (
    (pathname === '/dashboard/menu' || pathname === '/dashboard/actions') &&
    search.listingId !== own
  ) {
    return `${pathname}?listingId=${own}`
  }
  return null
}
